/**
 * Hook to fetch product order history for an account.
 *
 * Follows the same pattern as useAccountContacts — fetch on
 * accountUuid change, cleanup on unmount.
 */

import { useState, useEffect } from "react";
import type { AccountProductHistoryItem } from "../services/copilotProducts";
import { copilotProductService } from "../services/copilotProducts";

export interface UseAccountProductsReturn {
  products: AccountProductHistoryItem[];
  loading: boolean;
}

export function useAccountProducts(
  accountUuid: string | null,
): UseAccountProductsReturn {
  const [products, setProducts] = useState<AccountProductHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountUuid) {
      setProducts([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    copilotProductService
      .getAccountProductHistory(accountUuid)
      .then((response) => {
        if (!cancelled) {
          setProducts(response.products);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountUuid]);

  return { products, loading };
}
